import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Zap, DollarSign, Users, Clock } from 'lucide-react';
import SEO from '../components/SEO';

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="Pricing — $595/Course or $2,970 Full Diploma (Save $600)"
        description="6 courses at $595 each. Pay-as-you-go OR get the full PG Diploma upfront for $2,970 (save $600). 120 hours, $24.75/hour. 27x ROI vs. $80K+ starting salary. Course-by-course or full program."
        path="/pricing"
      />
      <div className="max-w-7xl mx-auto px-6 py-24">
        <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Investment</p>
        <h1 data-testid="pricing-heading" className="text-4xl md:text-5xl font-black tracking-tighter mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Transparent Pricing.
        </h1>
        <p className="text-lg text-white/90 mb-20 max-w-2xl">
          No hidden fees. Each course is independently priced, so you can learn at your own pace and budget.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#262626] mb-20">
          {/* Single Course */}
          <div data-testid="pricing-single" className="p-10 border-r border-[#262626] bg-[#111111]">
            <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-6" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Per Course</p>
            <p className="text-5xl font-black tracking-tight text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>$595</p>
            <p className="text-base text-zinc-200 mb-2">One-time payment</p>
            <p className="text-xs text-[#F59E0B] mb-8">Pay-as-you-go · Save $600 if you bundle all 6 →</p>
            <ul className="space-y-3 mb-8">
              {["20 hours instruction", "10 comprehensive modules", "Quizzes & assignments", "Certificate of completion"].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-base text-white/90">
                  <CheckCircle2 className="w-8 h-8 text-[#0077B6] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  {item}
                </li>
              ))}
            </ul>
            <Link to="/register" className="brand-btn w-full text-center text-sm py-3 block">Enroll in a Course</Link>
          </div>

          {/* Full Program */}
          <div data-testid="pricing-full" className="p-10 bg-[#0077B6] text-white border-r border-[#0077B6] relative">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#CC0000]" />
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-white/70 mb-6" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Full Diploma — Save $600</p>
            <p className="text-5xl font-black tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>$2,970</p>
            <p className="text-sm text-white/80 mb-1">All 6 courses · Paid upfront</p>
            <p className="text-xs text-white/60 mb-8 line-through">Regular: $3,570 ($595 × 6)</p>
            <ul className="space-y-3 mb-8">
              {["All 6 courses (120 hours)", "PG Diploma certificate", "AI-powered job matching", "Employer network access", "Priority career support", "Lifetime material access"].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                  <CheckCircle2 className="w-8 h-8 text-white mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  {item}
                </li>
              ))}
            </ul>
            <Link to="/register" className="w-full text-center text-sm py-3 block border-2 border-white text-white font-medium hover:bg-white hover:text-[#0077B6] transition-colors">
              Start Full Program
            </Link>
          </div>

          {/* ROI */}
          <div data-testid="pricing-roi" className="p-10 bg-[#111111]">
            <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-6" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Your ROI</p>
            <div className="space-y-8">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-200">Program Cost</span>
                  <span className="font-mono font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>$2,970</span>
                </div>
                <div className="h-2 bg-[#1a1a1a]">
                  <div className="h-2 bg-[#0077B6]" style={{ width: '4%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-200">Year 1 Salary</span>
                  <span className="font-mono font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>$80,000+</span>
                </div>
                <div className="h-2 bg-[#1a1a1a]">
                  <div className="h-2 bg-[#CC0000]" style={{ width: '100%' }} />
                </div>
              </div>
              <div className="pt-4 border-t border-[#262626]">
                <p className="text-7xl font-black tracking-tight text-[#F59E0B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>27x ROI</p>
                <p className="text-sm text-zinc-300 mt-1">Investment recovered in first month of employment</p>
              </div>
              <div className="pt-4 border-t border-[#262626] space-y-3">
                <div className="flex items-center gap-2 text-base text-white/90">
                  <DollarSign className="w-8 h-8 text-[#0077B6]" strokeWidth={1.5} />
                  <span>$24.50 per lecture hour</span>
                </div>
                <div className="flex items-center gap-2 text-base text-white/90">
                  <Clock className="w-8 h-8 text-[#0077B6]" strokeWidth={1.5} />
                  <span>120 total hours of training</span>
                </div>
                <div className="flex items-center gap-2 text-base text-white/90">
                  <Users className="w-8 h-8 text-[#0077B6]" strokeWidth={1.5} />
                  <span>Small cohort, personalized attention</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Plan */}
        <div data-testid="payment-plan" className="border border-[#262626] p-12 bg-[#111111]">
          <div className="flex items-start gap-4 mb-8">
            <Zap className="w-14 h-14 text-[#F59E0B] flex-shrink-0" strokeWidth={1.5} />
            <div>
              <h2 className="text-2xl font-bold tracking-tight mb-2 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Flexible Payment Strategy</h2>
              <p className="text-base text-white/80">We understand investing in education is significant. Here's how we make it manageable:</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Course-by-Course", desc: "Start with one $495 course. Add more as you progress and see results. No commitment to all 6 upfront." },
              { title: "Monthly Installments", desc: "Spread $495 across 3 monthly payments of ~$165 per course. Manageable alongside your daily expenses." },
              { title: "Employer Sponsorship", desc: "Many employers cover upskilling costs. We provide the documentation needed for reimbursement programs." },
            ].map((p, i) => (
              <div key={i} className="p-6 bg-[#0A0A0A] border border-[#262626]">
                <p className="font-semibold mb-2 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{p.title}</p>
                <p className="text-base text-white/80">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
